import { computed, effect, Injectable, OnDestroy, signal } from '@angular/core';
import { Firestore, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { docData } from 'rxfire/firestore';
import { Subscription } from 'rxjs';
import { DataService } from './data.service';
import { OrderService } from './order.service';
import { Table } from 'src/interfaces/table.model';

/** Причина вызова — как в приложении персонала */
export type WaiterCallReason = 'waiter' | 'bill' | 'help';

/** Состояние вызова у гостя: нет вызова, ждём ответа, официант идёт */
export type WaiterCallState = 'idle' | 'pending' | 'accepted';

/**
 * Вызов официанта: документ waiterCalls/{стол} в Firestore
 * (edim_app docs/design/README.md раздел 9)
 *
 * Гость выбирает причину — документ «Created»; уведомления, повторы через
 * 30 с, 60 с и 2 мин и «Иду» — у персонала и функций. Гость видит, что
 * вызов отправлен, и может отменить его; принят — «Официант идёт» и
 * позвать снова можно через 3 минуты от вызова (так же в правилах).
 */
@Injectable({
  providedIn: 'root'
})
export class WaiterCallService implements OnDestroy {
  /**
   * Свайп вызова виден: стол с включённой кнопкой, у стола есть активный
   * заказ (демо-заведение — всегда) и меню долистано до конца (маяк у
   * подвала). Так задумано — сначала гость смотрит меню, вызовов «на всякий
   * случай» меньше; без заказа — защита от ложных вызовов по ссылке или QR
   * с фото (то же проверяет onWaiterCallWritten). Активный вызов (ждём
   * ответа, официант идёт) виден всегда — menu-waiter-call
   */
  private footerVisible = signal<boolean>(false);
  private hasActiveOrder = signal<boolean>(false);
  showWaiterCall = computed(() =>
    this.footerVisible()
    && this.table?.waiterCall === true
    && (this.demo || this.hasActiveOrder()));

  /** Лист с причинами открыт */
  sheetOpen = signal<boolean>(false);

  private status = signal<'Created' | 'Accepted' | 'Cancelled' | null>(null);
  reason = signal<WaiterCallReason | null>(null);
  acceptedBy = signal<string>('');
  private createdAt = signal<number | null>(null);

  /** Сейчас — каждую секунду, для таймеров */
  private now = signal<number>(Date.now());
  private ticker = setInterval(() => this.now.set(Date.now()), 1000);

  /** Повторный вызов — не раньше, чем через 3 минуты от прежнего */
  private readonly cooldown = 3 * 60 * 1000;

  /** Сколько секунд ждёт вызов */
  waiting = computed(() => {
    const created = this.createdAt();
    return created === null ? 0 : Math.max(0, Math.floor((this.now() - created) / 1000));
  });

  /** Сколько секунд до повторного вызова */
  cooldownLeft = computed(() => {
    const created = this.createdAt();
    if (created === null) return 0;
    return Math.max(0, Math.ceil((created + this.cooldown - this.now()) / 1000));
  });

  state = computed<WaiterCallState>(() => {
    switch (this.status()) {
      case 'Created': return 'pending';
      case 'Accepted': return this.cooldownLeft() > 0 ? 'accepted' : 'idle';
      default: return 'idle';
    }
  });

  private table: Table | null = null;
  private subscription?: Subscription;
  private orderSubscription?: Subscription;

  constructor(
    private firestore: Firestore,
    private dataService: DataService,
    orderService: OrderService,
  ) {
    // Активный заказ стола (подписка OrderService — только активные статусы)
    this.orderSubscription = orderService.order$.subscribe((order) =>
      this.hasActiveOrder.set(order !== null));
    effect(() => {
      const table = this.dataService.getTable();
      if (table && table.id !== this.table?.id) {
        this.table = table;
        this.watch(table.id);
      }
    });
  }

  private get demo(): boolean {
    return this.dataService.restaurant?.demo === true;
  }

  /** Подписка на вызов стола: статус меняют официант («Иду») и функции */
  private watch(tableId: string): void {
    this.subscription?.unsubscribe();
    if (this.demo) return;
    this.subscription = docData(doc(this.firestore, 'waiterCalls', tableId)).subscribe({
      next: (data) => {
        const call = data as {
          status?: 'Created' | 'Accepted' | 'Cancelled';
          reason?: WaiterCallReason;
          createdAt?: { toMillis(): number } | null;
          acceptedBy?: { name?: string };
        } | undefined;
        this.status.set(call?.status ?? null);
        this.reason.set(call?.reason ?? null);
        this.acceptedBy.set(call?.acceptedBy?.name ?? '');
        // Своя запись ещё без времени сервера — сейчас
        this.createdAt.set(call ? call.createdAt?.toMillis() ?? Date.now() : null);
      },
      error: (error) => console.error('WaiterCallService | watch | error:', error),
    });
  }

  openSheet(): void {
    this.sheetOpen.set(true);
  }

  closeSheet(): void {
    this.sheetOpen.set(false);
  }

  /** Вызов с причиной [reason] */
  async call(reason: WaiterCallReason): Promise<void> {
    this.sheetOpen.set(false);
    const tableId = this.table?.id;
    const store = this.dataService.restaurant?.id;
    if (!tableId || !store) {
      console.warn('WaiterCallService | call | no table or restaurant');
      return;
    }

    // Демо: без записи — вызов «принимает» официант через 3 секунды
    if (this.demo) {
      this.reason.set(reason);
      this.createdAt.set(Date.now());
      this.status.set('Created');
      setTimeout(() => {
        if (this.status() !== 'Created') return;
        this.acceptedBy.set(this.dataService.restaurant?.title ?? '');
        this.status.set('Accepted');
      }, 3000);
      return;
    }

    try {
      await setDoc(doc(this.firestore, 'waiterCalls', tableId), {
        store,
        table: tableId,
        reason,
        status: 'Created',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('WaiterCallService | call | error:', error);
    }
  }

  /** Отменить вызов, пока его не приняли */
  async cancel(): Promise<void> {
    const tableId = this.table?.id;
    if (!tableId) return;
    if (this.demo) {
      this.status.set('Cancelled');
      return;
    }
    try {
      await updateDoc(doc(this.firestore, 'waiterCalls', tableId), {
        status: 'Cancelled',
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('WaiterCallService | cancel | error:', error);
    }
  }

  /** Номер стола — в листе причин */
  get tableIndex(): number | null {
    return this.table?.index ?? null;
  }

  // Маяк у подвала виден — меню долистано до конца
  setShowWaiterCall(visible: boolean): void {
    this.footerVisible.set(visible);
  }

  ngOnDestroy(): void {
    clearInterval(this.ticker);
    this.subscription?.unsubscribe();
    this.orderSubscription?.unsubscribe();
  }
}
